import { FC } from "react";
import moment from "moment";
import { classNames } from "utils";
import {
  CheckIcon,
  ClipboardDocumentListIcon,
  UserPlusIcon,
  XMarkIcon,
  PlusCircleIcon,
  ArrowPathRoundedSquareIcon,
} from "@heroicons/react/20/solid";

interface IssueHistoryViewProps {
  history: {
    userType:
      | "MeterContractor"
      | "MeterContractorUser"
      | "Contractor"
      | "ContractorUser"
      | "User";
    user: any;
    escalatedToType:
      | "MeterContractor"
      | "MeterContractorUser"
      | "Contractor"
      | "ContractorUser"
      | "User";
    escalatedTo: any;
    timestamp: string;
    notes: string;
    stage:
      | "Start"
      | "ReportedToFocalPerson"
      | "ReferredToPowerUser"
      | "ReassignedToPowerUser"
      | "EscalatedToPolymorphLabs"
      | "Resolved"
      | "Rejected"
      | "Reworking"
      | "Reassigned"
      | "Completed";
  }[];
}

const resolveHistoryActor = (
  type: any,
  details: { name: any; firstName: any; lastName: any; username: any }
) => {
  switch (type) {
    case "MeterContractor":
    case "Contractor": {
      return details?.name;
    }
    case "User":
    case "PartnerUser":
    case "MeterContractorUser": {
      return [details?.firstName, details?.lastName].join(" ");
    }
    case "ContractorUser": {
      return `${details?.firstName} ${details?.lastName} (${details?.username})`;
    }
    default:
      return "N/A";
  }
};

export const IssueHistoryView: FC<IssueHistoryViewProps> = ({ history }) => {
  const actionIconMap = {
    Start: (
      <span
        className={classNames(
          "bg-green-500",
          "h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white"
        )}
      >
        <PlusCircleIcon className="h-5 w-5 text-white" aria-hidden="true" />
      </span>
    ),
    ReportedToFocalPerson: (
      <span
        className={classNames(
          "bg-green-500",
          "h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white"
        )}
      >
        <UserPlusIcon className="h-5 w-5 text-white" aria-hidden="true" />
      </span>
    ),
    ReferredToPowerUser: (
      <span
        className={classNames(
          "bg-green-500",
          "h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white"
        )}
      >
        <UserPlusIcon className="h-5 w-5 text-white" aria-hidden="true" />
      </span>
    ),
    ReassignedToPowerUser: (
      <span
        className={classNames(
          "bg-green-500",
          "h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white"
        )}
      >
        <UserPlusIcon className="h-5 w-5 text-white" aria-hidden="true" />
      </span>
    ),
    EscalatedToPolymorphLabs: (
      <span
        className={classNames(
          "bg-green-500",
          "h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white"
        )}
      >
        <UserPlusIcon className="h-5 w-5 text-white" aria-hidden="true" />
      </span>
    ),
    Rejected: (
      <span
        className={classNames(
          "bg-red-500",
          "h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white"
        )}
      >
        <XMarkIcon className="h-5 w-5 text-white" aria-hidden="true" />
      </span>
    ),
    Resolved: (
      <span
        className={classNames(
          "bg-green-500",
          "h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white"
        )}
      >
        <ClipboardDocumentListIcon
          className="h-5 w-5 text-white"
          aria-hidden="true"
        />
      </span>
    ),
    Reassigned: (
      <span
        className={classNames(
          "bg-green-500",
          "h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white"
        )}
      >
        <UserPlusIcon className="h-5 w-5 text-white" aria-hidden="true" />
      </span>
    ),
    Reworking: (
      <span
        className={classNames(
          "bg-green-500",
          "h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white"
        )}
      >
        <ArrowPathRoundedSquareIcon
          className="h-5 w-5 text-white"
          aria-hidden="true"
        />
      </span>
    ),
    Completed: (
      <span
        className={classNames(
          "bg-green-500",
          "h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white"
        )}
      >
        <CheckIcon className="h-5 w-5 text-white" aria-hidden="true" />
      </span>
    ),
  };

  const renderActionDetails = (
    action: IssueHistoryViewProps["history"][number]
  ) => {
    switch (action.stage) {
      case "Start": {
        return (
          <p className="text-sm text-gray-500">
            <span className="font-medium text-gray-900">
              {resolveHistoryActor(action.userType, action.user)}
            </span>{" "}
            created issue
          </p>
        );
      }
      case "ReportedToFocalPerson": {
        return (
          <p className="text-sm text-gray-500">
            issue reported to{" "}
            <span className="font-medium text-gray-900">
              Focal Person {`(${resolveHistoryActor(action.escalatedToType, action.escalatedTo)})`}
            </span>
          </p>
        );
      }
      case "ReferredToPowerUser": {
        return (
          <p className="text-sm text-gray-500">
            issue refered to{" "}
            <span className="font-medium text-gray-900">
              Power User {`(${resolveHistoryActor(action.escalatedToType, action.escalatedTo)})`}
            </span>
          </p>
        );
      }
      case "ReassignedToPowerUser": {
        return (
          <p className="text-sm text-gray-500">
            issue reassigned to{" "}
            <span className="font-medium text-gray-900">
              Power User {`(${resolveHistoryActor(action.escalatedToType, action.escalatedTo)})`}
            </span>
          </p>
        );
      }
      case "Reassigned": {
        return (
          <p className="text-sm text-gray-500">
            issue has been re-assigned to{" "}
            <span className="font-medium text-gray-900">
              {`(${resolveHistoryActor(action.escalatedToType, action.escalatedTo)})`}
            </span>
          </p>
        );
      }
      case "EscalatedToPolymorphLabs": {
        return (
          <p className="text-sm text-gray-500">
            issue escalated to Polymorph Labs by{" "}
            <span className="font-medium text-gray-900">
              {resolveHistoryActor(action.userType, action.user)}
            </span>
          </p>
        );
      }
      case "Resolved": {
        return (
          <p className="text-sm text-gray-500">
            <span className="font-medium text-gray-900">
              {resolveHistoryActor(action.userType, action.user)}
            </span>{" "}
            resolved issue
          </p>
        );
      }
      case "Rejected": {
        return (
          <p className="text-sm text-gray-500">
            <span className="font-medium text-gray-900">
              {resolveHistoryActor(action.userType, action.user)}
            </span>{" "}
            rejected issue
          </p>
        );
      }
      case "Reworking": {
        return (
          <p className="text-sm text-gray-500">
            <span className="font-medium text-gray-900">
              {resolveHistoryActor(action.userType, action.user)}
            </span>{" "}
            is reworking issue
          </p>
        );
      }
      case "Completed": {
        return (
          <p className="text-sm text-gray-500">
            <span className="font-medium text-gray-900">
              {resolveHistoryActor(action.userType, action.user)}
            </span>{" "}
            approved resolved issue
          </p>
        );
      }

      default:
        return null;
    }
  };

  if (!history?.length) {
    return (
      <div
        className={classNames(
          false
            ? "border-amber-400 bg-amber-50 dark:border-amber-600"
            : "border-gray-300 dark:border-gray-600",
          "border-2 border-dashed py-2  rounded-md items-center justify-center flex flex-1 space-y-6 divide-y divide-gray-200 text-center text-sm"
        )}
      >
        No History Available For This Issue
      </div>
    );
  }

  return (
    <div className="flow-root p-6">
      <ul className="-mb-8">
        {history?.map((historyItem, historyItemIdx) => (
          <li key={historyItemIdx}>
            <div className="relative pb-8">
              {historyItemIdx !== history.length - 1 ? (
                <span
                  className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                  aria-hidden="true"
                />
              ) : null}
              <div className="relative flex space-x-3">
                <div>{actionIconMap[historyItem.stage]}</div>
                <div className="flex min-w-0 flex-1 justify-between space-x-12 pt-1.5">
                  <div className="flex-1">
                    {renderActionDetails(historyItem)}
                    {historyItem?.notes && (
                      <div className="relative p-4 border border-gray-300 rounded mt-6">
                        <span className="absolute bg-white px-2 -top-2 text-xs">
                          Notes
                        </span>
                        <p className="text-sm text-gray-600 font-light">
                          {historyItem.notes}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="whitespace-nowrap text-right text-sm flex flex-col text-gray-500">
                    <time dateTime={historyItem.timestamp}>
                      {moment(historyItem.timestamp).format("DD/MM/YYYY")}
                    </time>
                    <time dateTime={historyItem.timestamp}>
                      {moment(historyItem.timestamp).format("hh:mm A")}
                    </time>
                  </div>
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default IssueHistoryView;
